import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './auth';

export type OrgRole = 'owner' | 'admin' | 'manager' | 'contributor' | 'viewer';

export interface TeamDefinition {
  name: string;
  description: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  tagline: string | null;
  description: string | null;
  logo_url: string | null;
  address: string | null;
  phone: string | null;
  website: string | null;
  default_currency: string | null;
  default_tone_of_voice: string | null;
  industry_focus: string[] | null;
  settings: Record<string, unknown> | null;
  team_definitions: TeamDefinition[] | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrgRole;
  invited_by: string | null;
  invited_at: string | null;
  joined_at: string | null;
  created_at: string;
  // Joined from profiles
  profile?: {
    name: string | null;
    email: string;
  };
}

interface OrganizationContextType {
  organization: Organization | null;
  userRole: OrgRole | null;
  members: OrganizationMember[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  hasRole: (minRole: OrgRole) => boolean;
  // RBAC matrix — single source of truth for UI gating
  canManageBilling: boolean;      // owner
  canManageTeam: boolean;         // admin+
  canViewTeam: boolean;           // contributor+ (viewers cannot)
  canEditWorkspace: boolean;      // admin+
  canCreateProposal: boolean;     // contributor+
  canEditProposal: boolean;       // contributor+
  canDeleteProposal: boolean;     // contributor+
  canExportProposal: boolean;     // contributor+
  canEditVault: boolean;          // contributor+
  canDeleteVault: boolean;        // contributor+
  canGenerateAI: boolean;         // contributor+
  canComment: boolean;            // viewer+ (any member)
  canResolveComments: boolean;    // contributor+
  canViewAnalytics: boolean;      // contributor+
  canTransferOwnership: boolean;  // owner
  // Legacy aliases — kept for back-compat during sweep
  canEdit: boolean;
  canDelete: boolean;
  isOwner: boolean;
  isAdmin: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// Role hierarchy (higher number = more permissions)
const roleHierarchy: Record<OrgRole, number> = {
  owner: 5,
  admin: 4,
  manager: 3,
  contributor: 2,
  viewer: 1,
};

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userRole, setUserRole] = useState<OrgRole | null>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrganization = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1000;

    if (!user?.id) {
      setOrganization(null);
      setUserRole(null);
      setMembers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // First, get the user's organization membership
      const { data: membershipData, error: membershipError } = await supabase
        .from('organization_members')
        .select('organization_id, role')
        .eq('user_id', user.id)
        .not('joined_at', 'is', null)
        .order('joined_at', { ascending: true })
        .limit(1)
        .single();

      if (membershipError) {
        // No organization yet - this could be a race condition for new users
        // The database trigger may not have completed yet
        if (membershipError.code === 'PGRST116') {
          if (retryCount < MAX_RETRIES) {
            
            setTimeout(() => {
              fetchOrganization(retryCount + 1);
            }, RETRY_DELAY_MS * (retryCount + 1)); // Exponential backoff
            return;
          }
          // After max retries, set null state
          setOrganization(null);
          setUserRole(null);
          setMembers([]);
          setLoading(false);
          return;
        }
        throw membershipError;
      }

      setUserRole(membershipData.role as OrgRole);

      // Fetch the organization details
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', membershipData.organization_id)
        .single();

      if (orgError) throw orgError;
      
      // Cast JSONB fields properly from Supabase types
      const organization: Organization = {
        id: orgData.id,
        name: orgData.name,
        slug: orgData.slug,
        tagline: orgData.tagline,
        description: orgData.description,
        logo_url: orgData.logo_url,
        address: orgData.address,
        phone: orgData.phone,
        website: orgData.website,
        default_currency: orgData.default_currency,
        default_tone_of_voice: orgData.default_tone_of_voice,
        industry_focus: orgData.industry_focus,
        settings: orgData.settings as Record<string, unknown> | null,
        team_definitions: Array.isArray(orgData.team_definitions) 
          ? (orgData.team_definitions as unknown as TeamDefinition[]) 
          : null,
        created_at: orgData.created_at,
        updated_at: orgData.updated_at,
      };
      setOrganization(organization);

      // Fetch all members with their profile info
      const { data: membersData, error: membersError } = await supabase
        .from('organization_members')
        .select(`
          id,
          organization_id,
          user_id,
          role,
          invited_by,
          invited_at,
          joined_at,
          created_at
        `)
        .eq('organization_id', membershipData.organization_id)
        .not('joined_at', 'is', null)
        .order('joined_at', { ascending: true });

      if (membersError) throw membersError;

      // Fetch profiles for all members
      const userIds = membersData.map(m => m.user_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Merge profiles with members
      const membersWithProfiles = membersData.map(member => ({
        ...member,
        role: member.role as OrgRole,
        profile: profilesData?.find(p => p.id === member.user_id) || undefined,
      }));

      setMembers(membersWithProfiles);
    } catch (err) {
      console.error('Failed to fetch organization:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch organization'));
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  // Mirror active org id to localStorage so non-React utilities
  // (e.g. productAnalytics.trackEvent) can attribute events to the org.
  useEffect(() => {
    try {
      if (organization?.id) {
        localStorage.setItem('sorvaine_active_org_id', organization.id);
      } else {
        localStorage.removeItem('sorvaine_active_org_id');
      }
    } catch {
      /* ignore storage errors */
    }
  }, [organization?.id]);

  const hasRole = useCallback((minRole: OrgRole): boolean => {
    if (!userRole) return false;
    return roleHierarchy[userRole] >= roleHierarchy[minRole];
  }, [userRole]);

  const value: OrganizationContextType = {
    organization,
    userRole,
    members,
    loading,
    error,
    refetch: fetchOrganization,
    hasRole,
    canManageBilling: userRole === 'owner',
    canManageTeam: hasRole('admin'),
    canViewTeam: hasRole('contributor'),
    canEditWorkspace: hasRole('admin'),
    canCreateProposal: hasRole('contributor'),
    canEditProposal: hasRole('contributor'),
    canDeleteProposal: hasRole('contributor'),
    canExportProposal: hasRole('contributor'),
    canEditVault: hasRole('contributor'),
    canDeleteVault: hasRole('contributor'),
    canGenerateAI: hasRole('contributor'),
    canComment: hasRole('viewer'),
    canResolveComments: hasRole('contributor'),
    canViewAnalytics: hasRole('contributor'),
    canTransferOwnership: userRole === 'owner',
    canEdit: hasRole('contributor'),
    canDelete: hasRole('contributor'),
    isOwner: userRole === 'owner',
    isAdmin: userRole === 'admin' || userRole === 'owner',
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}

// Helper hook to get organization ID for queries
export function useOrganizationId(): string | null {
  const { organization } = useOrganization();
  return organization?.id ?? null;
}
